import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TourProps } from 'antd';
import {
  Alert as AntAlert,
  AutoComplete,
  Button,
  Checkbox,
  Drawer,
  Grid,
  Input,
  InputNumber,
  List,
  Modal,
  Popover,
  Segmented,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DownOutlined,
  DownloadOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  RobotOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import LoadingCard from './LoadingCard';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import {
  normalizeBookTermLists,
  normalizeTargetLangKey,
  resolveAccountTerms,
  resolveBookTerms,
  resolveSeriesTerms,
} from '../utils/glossaryKeys';
import { splitLanguagePair } from '../utils/languagePairs';
import { useTourFlags } from '../tours/useTourFlags';
import './TermScanPanel.css';
import { isActiveGlossaryScan } from '../utils/glossaryScan';

const { Text } = Typography;
const { useBreakpoint } = Grid;

type TermAction = 'ignore' | 'never' | 'always';
type TermClassification = 'always' | 'never' | 'uncertain';
type StatusFilter = 'all' | 'pending' | 'applied' | 'skipped' | 'covered';
type SortOption = 'term-asc' | 'occurrences-desc';
type ScopeOption = 'book' | 'series' | 'account';
type ScopeFilter = 'all' | ScopeOption | 'unknown';
type GlossaryConflict =
  | 'series-override'
  | 'conflict-always'
  | 'conflict-never'
  | 'conflict-already-always';

const AUTO_APPLY_CONFIDENCE_THRESHOLD = 0.85;

type TermSuggestion = {
  id: string;
  chapterGuid?: string;
  chapterNumber?: number;
  chapterName?: string;
  sourceTerm: string;
  targets?: Array<{ text: string; meaning?: string; rank?: number; action?: string }>;
  aiStatus?: string;
  ai_status?: string;
  recommendedAction?: TermAction;
  classification?: TermClassification;
  optionsCount?: number | string;
  confidence?: number;
  translation?: string;
  target?: string;
  targetText?: string;
  hasMultipleSuggestions?: boolean;
  occurrenceCount?: number;
  status?: string;
  snippet?: string;
  contextSnippets?: string[];
  chapterReferences?: Array<{ chapterGuid?: string; chapterNumber?: number; chapterName?: string }>;
  rationale?: string;
  regeneratedAt?: string;
  enrichmentRepair?: string;
  seriesCoverage?: 'always' | 'never';
  decision?: { action?: TermAction; scopes?: ScopeOption[]; targetText?: string; reason?: string };
};

type TermScanConflict = {
  suggestionId?: string;
  reason?: string;
  scope?: string;
};

const isSeriesCoveredSuggestion = (item: TermSuggestion): boolean =>
  item.decision?.reason === 'series-override' ||
  item.seriesCoverage === 'always' ||
  item.seriesCoverage === 'never';

const isSkippedSuggestion = (item: TermSuggestion): boolean =>
  item.status === 'ignored' && !isSeriesCoveredSuggestion(item);

const isLockedSuggestion = (item: TermSuggestion): boolean =>
  item.status === 'applied' || isSeriesCoveredSuggestion(item);

const isManuallyActionableSuggestion = (item: TermSuggestion): boolean =>
  !item.status || item.status === 'pending' || isSkippedSuggestion(item);

const normalizeTerm = (value: string | null | undefined): string =>
  (value ?? '').toString().trim().toLowerCase().replace(/\s+/g, ' ');

const getPairSource = (value: string | null | undefined): string => {
  const raw = (value ?? '').toString();
  const [source = ''] = raw.split('|');
  return source.trim();
};

const normalizePairSource = (value: string | null | undefined): string =>
  normalizeTerm(getPairSource(value));

const normalizeScopeOption = (value: unknown): ScopeOption | null => {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (normalized === 'book' || normalized === 'series' || normalized === 'account') {
    return normalized;
  }
  return null;
};

const formatScopeOptionLabel = (scope: ScopeOption): string => {
  if (scope === 'series') return 'Series';
  if (scope === 'account') return 'Account';
  return 'Book';
};

export const resolveSuggestionDecisionScopes = (
  decision?: { scopes?: Array<ScopeOption | string | null | undefined> } | null
): ScopeOption[] => {
  if (!decision || !Array.isArray(decision.scopes)) return [];
  return Array.from(
    new Set(
      decision.scopes
        .map((scopeValue) => normalizeScopeOption(scopeValue))
        .filter(Boolean) as ScopeOption[]
    )
  );
};

export const matchesSuggestionScopeFilter = (
  item: { decision?: { scopes?: Array<ScopeOption | string | null | undefined> } | null },
  scopeFilter: ScopeFilter
): boolean => {
  if (scopeFilter === 'all') return true;
  const scopes = resolveSuggestionDecisionScopes(item.decision);
  if (!scopes.length) {
    return scopeFilter === 'unknown';
  }
  if (scopeFilter === 'unknown') return false;
  return scopes.includes(scopeFilter);
};

const reportConflicts = (conflicts: TermScanConflict[] = []) => {
  if (!conflicts.length) return;
  const seriesCount = conflicts.filter((conflict) => conflict.reason === 'series-override').length;
  const alwaysCount = conflicts.filter((conflict) => conflict.reason === 'conflict-always').length;
  const neverCount = conflicts.filter((conflict) => conflict.reason === 'conflict-never').length;
  const alreadyAlwaysCount = conflicts.filter((conflict) => conflict.reason === 'conflict-already-always').length;
  const conflictCount = alwaysCount + neverCount + alreadyAlwaysCount;
  if (conflictCount) {
    message.warning(
      `Left ${conflictCount} suggestion${conflictCount > 1 ? 's' : ''} unchanged because of glossary conflicts.`
    );
  }
  if (seriesCount) {
    message.info(
      `Left ${seriesCount} suggestion${seriesCount > 1 ? 's' : ''} unchanged because the series glossary already covers ${seriesCount > 1 ? 'them' : 'it'}.`
    );
  }
};

const shouldFlipAlwaysToNever = (item: TermSuggestion): boolean => {
  const rawAction = (item.recommendedAction || item.classification || '').toString().toLowerCase();
  if (rawAction !== 'always') return false;
  const targets = item.targets || [];
  if (targets.length !== 1) return false;
  const source = normalizeTerm(item.sourceTerm);
  const target = normalizeTerm(targets[0]?.text);
  return Boolean(source && target && source === target);
};

const getRecommendedAction = (item: TermSuggestion): TermAction | 'ignore' => {
  const raw = (item.recommendedAction || '').toString().toLowerCase();
  if (raw === 'always' || raw === 'never' || raw === 'ignore') {
    if (raw === 'always' && shouldFlipAlwaysToNever(item)) return 'never';
    return raw as TermAction;
  }
  return 'ignore';
};

const isAiUncertain = (item: TermSuggestion): boolean => {
  const aiStatusRaw = (item.aiStatus || item.ai_status || '').toString().toLowerCase();
  return aiStatusRaw.includes('uncertain') || item.classification === 'uncertain';
};

const getOptionsCount = (item: TermSuggestion): number | null => {
  if (typeof item.optionsCount === 'number' && Number.isFinite(item.optionsCount)) {
    return item.optionsCount;
  }
  if (typeof item.optionsCount === 'string') {
    const parsed = Number(item.optionsCount);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (Array.isArray(item.targets)) {
    return item.targets.length;
  }
  return null;
};

const isMultiOption = (item: TermSuggestion): boolean => {
  const optionsCount = getOptionsCount(item);
  if (optionsCount != null && optionsCount > 1) return true;
  return Boolean(item.hasMultipleSuggestions);
};

const isMissingTranslation = (item: TermSuggestion): boolean => {
  let action = (item.recommendedAction || item.classification || '').toString().toLowerCase();
  if (action === 'always' && shouldFlipAlwaysToNever(item)) {
    action = 'never';
  }
  if (action !== 'always') return false;
  const translation = (
    item.translation ||
    item.targetText ||
    item.target ||
    (item.targets && item.targets[0] && item.targets[0].text) ||
    ''
  )
    .toString()
    .trim();
  return !translation;
};

const isUncertain = (item: TermSuggestion): boolean => {
  if (isAiUncertain(item)) return true;
  if (isMissingTranslation(item)) return true;
  return false;
};

const getSuggestionConfidence = (item: TermSuggestion): number | null => {
  const raw = item.confidence;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return raw > 1 ? Math.max(0, Math.min(raw / 100, 1)) : Math.max(0, Math.min(raw, 1));
};

const isAutoApplyCandidate = (item: TermSuggestion): boolean => {
  const action = getRecommendedAction(item);
  if (action !== 'always' && action !== 'never') {
    return false;
  }
  if (isMissingTranslation(item)) {
    return false;
  }
  const confidence = getSuggestionConfidence(item);
  return !isAiUncertain(item) && confidence != null && confidence >= AUTO_APPLY_CONFIDENCE_THRESHOLD;
};

const getDownloadFilename = (contentDisposition?: string, fallback = 'glossary-scan.docx'): string => {
  const header = (contentDisposition || '').toString();
  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/"/g, '').trim()) || fallback;
    } catch (_err) {
      return utf8Match[1].replace(/"/g, '').trim() || fallback;
    }
  }
  const filenameMatch = header.match(/filename="?([^";]+)"?/i);
  return filenameMatch?.[1]?.trim() || fallback;
};

interface TermScanPanelProps {
  bookId: string;
  pair: string;
  apiBase: string;
  auth: any;
  selectedSeriesId?: string | null;
  resolvedUserId?: string;
  neverTranslateWords: { [key: string]: string[] };
  alwaysTranslateWords: { [key: string]: string[] };
  accountNeverTranslateWords?: { [key: string]: string[] };
  accountAlwaysTranslateWords?: { [key: string]: string[] };
  seriesNeverTranslateWords?: { [key: string]: string[] };
  seriesAlwaysTranslateWords?: { [key: string]: string[] };
  setNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAccountNeverTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setAccountAlwaysTranslateWords?: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  setSeriesTranslateRules?: React.Dispatch<
    React.SetStateAction<{
      alwaysTranslate: Record<string, string[]>;
      neverTranslate: Record<string, string[]>;
    }>
  >;
  runInfo?: any | null;
  suggestionsProp?: TermSuggestion[];
  onApplyingChange?: (applying: boolean) => void;
  onTourControlChange?: (openTour: (() => void) | null) => void;
}

const TermScanPanel: React.FC<TermScanPanelProps> = ({
  bookId,
  pair,
  apiBase,
  auth,
  selectedSeriesId,
  resolvedUserId,
  neverTranslateWords,
  alwaysTranslateWords,
  accountNeverTranslateWords = {},
  accountAlwaysTranslateWords = {},
  seriesNeverTranslateWords = {},
  seriesAlwaysTranslateWords = {},
  setNeverTranslateWords,
  setAlwaysTranslateWords,
  setAccountNeverTranslateWords,
  setAccountAlwaysTranslateWords,
  setSeriesTranslateRules,
  runInfo,
  suggestionsProp,
  onApplyingChange,
  onTourControlChange,
}) => {
  const [run, setRun] = useState<any | null>(runInfo || null);
  const [suggestions, setSuggestions] = useState<TermSuggestion[]>(suggestionsProp || []);
  const [decisions, setDecisions] = useState<Record<string, TermAction>>({});
  const [targetOverrides, setTargetOverrides] = useState<Record<string, string>>({});
  const [applying, setApplying] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [scope, setScope] = useState<ScopeOption>('book');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const hasInjectedSuggestions = Array.isArray(suggestionsProp);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [applySummary, setApplySummary] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [onlyMultiOption, setOnlyMultiOption] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('occurrences-desc');
  const [minOccurrences, setMinOccurrences] = useState<number | null>(null);
  const [uncertainOnly, setUncertainOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});
  const [dirtyIds, setDirtyIds] = useState<Record<string, boolean>>({});
  const [regeneratingIds, setRegeneratingIds] = useState<Record<string, boolean>>({});
  const [tourOpen, setTourOpen] = useState(false);
  const listContainerRef = useRef<HTMLDivElement | null>(null);

  const { sourceLang, targetLang } = useMemo(() => splitLanguagePair(pair), [pair]);

  const canScan = Boolean(bookId && sourceLang && targetLang);
  const userId = resolvedUserId || auth?.currentUser?.uid || '';
  const {
    flags: tourFlags,
    initialized: tourFlagsInitialized,
    markSeen: markTourSeen,
  } = useTourFlags(userId || null, apiBase);
  const hasSeenTermScanTour = Boolean(tourFlags.termScanPanelTourSeen);
  const tourFallbackRef = useRef<HTMLDivElement | null>(null);
  const canApplySeries = Boolean(selectedSeriesId);
  const canApplyAccount = Boolean(userId);
  const effectiveScope: ScopeOption = scope === 'series' && !selectedSeriesId ? 'book' : scope;
  const pageSize = 50;
  const glossaryPairKey = pair === 'original' ? '' : pair;
  const targetLangKey = normalizeTargetLangKey(targetLang);
  const bookAlwaysPairs = glossaryPairKey ? resolveBookTerms(alwaysTranslateWords, glossaryPairKey) : [];
  const bookNeverTerms = glossaryPairKey ? resolveBookTerms(neverTranslateWords, glossaryPairKey) : [];
  const accountNeverTerms = targetLangKey
    ? resolveAccountTerms(accountNeverTranslateWords, targetLang)
    : [];
  const accountAlwaysPairs = targetLangKey
    ? resolveAccountTerms(accountAlwaysTranslateWords, targetLang)
    : [];
  const seriesNeverTerms = glossaryPairKey
    ? resolveSeriesTerms(seriesNeverTranslateWords, targetLang, glossaryPairKey)
    : [];
  const seriesAlwaysPairs = glossaryPairKey
    ? resolveSeriesTerms(seriesAlwaysTranslateWords, targetLang, glossaryPairKey)
    : [];
  const screens = useBreakpoint();
  const showStatusTabs = Boolean(screens.md);
  const useDrawerFilters = !showStatusTabs;
  const controlSize = 'middle' as const;

  const neverConflictSet = useMemo(
    () =>
      new Set(
        [...bookNeverTerms, ...seriesNeverTerms, ...accountNeverTerms]
          .map((term) => normalizeTerm(term))
          .filter(Boolean)
      ),
    [accountNeverTerms, bookNeverTerms, seriesNeverTerms]
  );

  const alwaysConflictSet = useMemo(
    () =>
      new Set(
        [...bookAlwaysPairs, ...seriesAlwaysPairs, ...accountAlwaysPairs]
          .map((pairValue) => normalizePairSource(pairValue))
          .filter(Boolean)
      ),
    [accountAlwaysPairs, bookAlwaysPairs, seriesAlwaysPairs]
  );

  const bookAlwaysSourceSet = useMemo(
    () => new Set(bookAlwaysPairs.map((pairValue) => normalizePairSource(pairValue)).filter(Boolean)),
    [bookAlwaysPairs]
  );

  const accountAlwaysSourceSet = useMemo(
    () => new Set(accountAlwaysPairs.map((pairValue) => normalizePairSource(pairValue)).filter(Boolean)),
    [accountAlwaysPairs]
  );

  const seriesNeverSet = useMemo(
    () => new Set(seriesNeverTerms.map((term) => normalizeTerm(term)).filter(Boolean)),
    [seriesNeverTerms]
  );

  const seriesAlwaysSourceSet = useMemo(
    () =>
      new Set(seriesAlwaysPairs.map((pairValue) => normalizePairSource(pairValue)).filter(Boolean)),
    [seriesAlwaysPairs]
  );

  const getConflictReason = useCallback(
    (term: string, action: TermAction, activeScope: ScopeOption): GlossaryConflict | null => {
      if (action !== 'always' && action !== 'never') return null;
      const normalized = normalizeTerm(term);
      if (!normalized) return null;
      if (action === 'always') {
        if (neverConflictSet.has(normalized)) return 'conflict-never';
        if (activeScope === 'book' && seriesAlwaysSourceSet.has(normalized)) return 'series-override';
        if (activeScope === 'book' && bookAlwaysSourceSet.has(normalized)) return 'conflict-already-always';
        if (activeScope === 'series' && seriesAlwaysSourceSet.has(normalized)) return 'conflict-already-always';
        if (activeScope === 'account' && accountAlwaysSourceSet.has(normalized)) return 'conflict-already-always';
      }
      if (action === 'never') {
        if (alwaysConflictSet.has(normalized)) return 'conflict-always';
        if (activeScope === 'book' && seriesNeverSet.has(normalized)) return 'series-override';
      }
      return null;
    },
    [
      accountAlwaysSourceSet,
      alwaysConflictSet,
      bookAlwaysSourceSet,
      neverConflictSet,
      seriesAlwaysSourceSet,
      seriesNeverSet,
    ]
  );

  const updateDecisionsFromSuggestions = useCallback((items: TermSuggestion[]) => {
    if (!items.length) return;
    setDecisions((prev) => {
      const next: Record<string, TermAction> = { ...prev };
      items.forEach((item) => {
        if (!next[item.id]) {
          const fallbackAction = item.decision?.action || getRecommendedAction(item);
          next[item.id] = fallbackAction;
        }
      });
      return next;
    });
  }, []);

  const getClassification = useCallback((item: TermSuggestion): TermClassification => {
    const raw = item.classification || (item.recommendedAction === 'ignore' ? 'uncertain' : item.recommendedAction);
    if (raw === 'always' && shouldFlipAlwaysToNever(item)) return 'never';
    if (raw === 'always' || raw === 'never' || raw === 'uncertain') return raw;
    if (item.recommendedAction === 'always' || item.recommendedAction === 'never') {
      return shouldFlipAlwaysToNever(item) ? 'never' : item.recommendedAction;
    }
    return 'uncertain';
  }, []);

  const dedupeSuggestions = useCallback((items: TermSuggestion[]) => {
    const flattenedMap = new Map<string, TermSuggestion>();
    items.forEach((suggestion) => {
      const key = suggestion.id || suggestion.sourceTerm;
      if (!key || flattenedMap.has(key)) return;
      flattenedMap.set(key, suggestion);
    });
    return Array.from(flattenedMap.values());
  }, []);

  const fetchRun = useCallback(async () => {
    if (!run?.runId || !canScan || !userId) return;
    setLoading(true);
    setFetchError(null);
    try {
      const resp = await axios.get(`${apiBase}/books/${bookId}/term-scan/${run.runId}`, {
        params: {
          userId,
        },
      });
      const data = resp?.data || {};
      if (data.run) {
        setRun((prev: any) => ({ ...(prev || {}), ...data.run }));
      }
      const incomingSuggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
      const deduped = dedupeSuggestions(incomingSuggestions);
      setSuggestions(deduped);
      updateDecisionsFromSuggestions(deduped);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to load term scan run', err);
      setFetchError(err?.response?.data?.detail || err?.message || 'Unable to load scan status.');
    } finally {
      setLoading(false);
    }
  }, [apiBase, bookId, canScan, dedupeSuggestions, run?.runId, updateDecisionsFromSuggestions, userId]);

  const handleExportDocx = useCallback(async () => {
    if (!run?.runId || !userId) return;
    const currentStatus = (run?.status || '').toString().toLowerCase();
    if (isActiveGlossaryScan(currentStatus)) {
      message.info('Wait for the AI glossary scan to finish before exporting.');
      return;
    }
    setExportingDocx(true);
    try {
      const resp = await axios.get(`${apiBase}/books/${bookId}/term-scan/${run.runId}/export-docx`, {
        params: { userId },
        responseType: 'blob',
      });
      const headers = (resp as any)?.headers || {};
      const filename = getDownloadFilename(
        headers['content-disposition'] || headers['Content-Disposition'],
        'glossary-scan.docx'
      );
      const blob = resp.data instanceof Blob
        ? resp.data
        : new Blob([resp.data], {
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Glossary scan exported to Word.');
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to export glossary scan', err);
      message.error(err?.response?.data?.detail || err?.message || 'Unable to export glossary scan.');
    } finally {
      setExportingDocx(false);
    }
  }, [apiBase, bookId, run?.runId, run?.status, userId]);

  useEffect(() => {
    if (!runInfo?.runId) return;
    setRun(runInfo);
    setFetchError(null);
    setApplySummary(null);
    setSelectedIds({});
  }, [runInfo]);

  useEffect(() => {
    if (suggestionsProp) {
      const deduped = dedupeSuggestions(suggestionsProp);
      setSuggestions(deduped);
      updateDecisionsFromSuggestions(deduped);
    }
  }, [suggestionsProp, dedupeSuggestions, updateDecisionsFromSuggestions]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const next: Record<string, boolean> = {};
      suggestions.forEach((item) => {
        if (item.id && prev[item.id]) {
          next[item.id] = true;
        }
      });
      return next;
    });
  }, [suggestions]);

  useEffect(() => {
    onApplyingChange?.(applying);
  }, [applying, onApplyingChange]);

  useEffect(() => {
    let preferred: ScopeOption = 'book';
    if (userId) {
      try {
        preferred = normalizeScopeOption(localStorage.getItem(`scribeshadow:term-scan-scope:v1:${userId}`)) || 'book';
      } catch {
        // Storage may be unavailable; the selector remains usable.
      }
    }
    setScope(preferred === 'series' && !canApplySeries ? 'book' : preferred);
  }, [userId, canApplySeries]);

  const handleScopeChange = (value: ScopeOption) => {
    setScope(value);
    if (userId) {
      try {
        localStorage.setItem(`scribeshadow:term-scan-scope:v1:${userId}`, value);
      } catch {
        // Remembering a preference must not block glossary work.
      }
    }
  };

  useEffect(() => {
    if (statusFilter !== 'pending' && uncertainOnly) {
      setUncertainOnly(false);
    }
  }, [statusFilter, uncertainOnly]);

  const clearSelection = useCallback(() => {
    setSelectedIds({});
  }, []);

  const scrollListToTop = useCallback(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, []);

  const renderTeacherPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );

  const getTourTarget = useCallback(
    (ids: string[]) => {
      if (typeof document !== 'undefined') {
        for (const id of ids) {
          const el = document.querySelector(`[data-tour-id="${id}"]`);
          if (el) return el as HTMLElement;
        }
      }
      return (
        tourFallbackRef.current ||
        (typeof document !== 'undefined' ? document.body : null)
      ) as HTMLElement;
    },
    []
  );

  const handleOpenTour = useCallback(() => {
    setTourOpen(true);
  }, []);

  useEffect(() => {
    if (!onTourControlChange) return undefined;
    onTourControlChange(handleOpenTour);
    return () => onTourControlChange(null);
  }, [handleOpenTour, onTourControlChange]);

  const handleCloseTour = useCallback(() => {
    setTourOpen(false);
    void markTourSeen('termScanPanelTourSeen');
  }, [markTourSeen]);

  const handleStatusChange = (value: StatusFilter) => {
    setStatusFilter(value);
    setCurrentPage(1);
    clearSelection();
  };

  const filteredSuggestions = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    let items = suggestions;
    if (needle) {
      items = items.filter((item) => (item.sourceTerm || '').toLowerCase().includes(needle));
    }
    if (statusFilter !== 'all') {
      items = items.filter((item) => {
        if (statusFilter === 'pending' || statusFilter === 'applied') {
          return (item.status || 'pending') === statusFilter;
        }
        if (statusFilter === 'skipped') {
          return isSkippedSuggestion(item);
        }
        return item.status === 'ignored' && isSeriesCoveredSuggestion(item);
      });
    }
    if (scopeFilter !== 'all') {
      items = items.filter((item) => matchesSuggestionScopeFilter(item, scopeFilter));
    }
    if (uncertainOnly && statusFilter === 'pending') {
      items = items.filter(isUncertain);
    }
    if (onlyMultiOption) {
      items = items.filter((item) => (item.targets || []).length > 1);
    }
    if (typeof minOccurrences === 'number' && Number.isFinite(minOccurrences)) {
      items = items.filter((item) => {
        const count = typeof item.occurrenceCount === 'number' ? item.occurrenceCount : 0;
        return count >= minOccurrences;
      });
    }
    const sorted = [...items].sort((a, b) => {
      if (sortOption === 'occurrences-desc') {
        const countA = typeof a.occurrenceCount === 'number' ? a.occurrenceCount : 0;
        const countB = typeof b.occurrenceCount === 'number' ? b.occurrenceCount : 0;
        if (countA !== countB) {
          return countB - countA;
        }
      }
      return (a.sourceTerm || '').localeCompare(b.sourceTerm || '', undefined, { sensitivity: 'base' });
    });
    return sorted;
  }, [minOccurrences, onlyMultiOption, scopeFilter, searchTerm, sortOption, statusFilter, suggestions, uncertainOnly]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredSuggestions.length / pageSize));
    if (currentPage > maxPage) {
      setCurrentPage(1);
    }
  }, [currentPage, filteredSuggestions.length, pageSize]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSuggestions.slice(start, start + pageSize);
  }, [currentPage, filteredSuggestions, pageSize]);

  const tutorialFirstSuggestionId = pageItems[0]?.id || null;
  const tutorialAlwaysSuggestionId = useMemo(() => {
    const alwaysSuggestion = pageItems.find((item) => {
      if (isLockedSuggestion(item)) return false;
      const action = decisions[item.id] || getRecommendedAction(item);
      return action === 'always';
    });
    return alwaysSuggestion?.id || null;
  }, [decisions, pageItems]);

  const pageIds = useMemo(() => pageItems.map((item) => item.id).filter(Boolean) as string[], [pageItems]);
  const selectedCount = useMemo(
    () => Object.values(selectedIds).filter(Boolean).length,
    [selectedIds]
  );
  const selectedActionableCount = useMemo(
    () => suggestions.filter((item) => selectedIds[item.id] && isManuallyActionableSuggestion(item)).length,
    [selectedIds, suggestions]
  );
  const dirtyCount = useMemo(
    () => suggestions.filter((item) => dirtyIds[item.id] && isManuallyActionableSuggestion(item)).length,
    [dirtyIds, suggestions]
  );
  const pendingCount = useMemo(
    () => suggestions.filter((item) => !item.status || item.status === 'pending').length,
    [suggestions]
  );
  const appliedCount = useMemo(
    () => suggestions.filter((item) => item.status === 'applied').length,
    [suggestions]
  );
  const ignoredCount = useMemo(
    () => suggestions.filter((item) => item.status === 'ignored').length,
    [suggestions]
  );
  const manualSkippedCount = useMemo(
    () => suggestions.filter(isSkippedSuggestion).length,
    [suggestions]
  );
  const seriesCoveredCount = useMemo(
    () => suggestions.filter((item) => item.status === 'ignored' && isSeriesCoveredSuggestion(item)).length,
    [suggestions]
  );
  const pageSelectedCount = useMemo(
    () => pageIds.filter((id) => selectedIds[id]).length,
    [pageIds, selectedIds]
  );
  const allPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;
  const somePageSelected = pageSelectedCount > 0 && pageSelectedCount < pageIds.length;

  useEffect(() => {
    if (statusFilter === 'pending' && suggestions.length > 0 && pendingCount === 0 && ignoredCount > 0) {
      setStatusFilter(manualSkippedCount > 0 ? 'skipped' : 'covered');
    }
  }, [ignoredCount, manualSkippedCount, pendingCount, statusFilter, suggestions.length]);

  useEffect(() => {
    if (!run?.runId || !canScan) return;
    let cancelled = false;
    const status = (run?.status || 'queued').toLowerCase();
    const shouldPoll = isActiveGlossaryScan(status);
    if (hasInjectedSuggestions && !shouldPoll) return;
    const doFetch = async () => {
      if (cancelled) return;
      await fetchRun();
    };
    doFetch();
    if (!shouldPoll) return;
    const interval = window.setInterval(doFetch, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [run?.runId, run?.status, canScan, fetchRun, hasInjectedSuggestions]);

  const updateLocalTerms = (payload?: {
    bookTerms?: any;
    accountTerms?: any;
    seriesTerms?: any;
  }) => {
    if (!payload) return;
    const bookTerms = payload.bookTerms;
    if (bookTerms?.neverTranslate && setNeverTranslateWords) {
      const mappedNever = normalizeBookTermLists(bookTerms.neverTranslate);
      setNeverTranslateWords((prev) => ({ ...prev, ...mappedNever }));
    }
    if (bookTerms?.alwaysTranslate && setAlwaysTranslateWords) {
      const mappedAlways = normalizeBookTermLists(bookTerms.alwaysTranslate);
      setAlwaysTranslateWords((prev) => ({ ...prev, ...mappedAlways }));
    }
    const accountTerms = payload.accountTerms;
    if (accountTerms?.neverTranslate && setAccountNeverTranslateWords) {
      setAccountNeverTranslateWords((prev) => ({ ...prev, ...accountTerms.neverTranslate }));
    }
    if (accountTerms?.alwaysTranslate && setAccountAlwaysTranslateWords) {
      setAccountAlwaysTranslateWords((prev) => ({ ...prev, ...accountTerms.alwaysTranslate }));
    }
    const seriesTerms = payload.seriesTerms;
    if (seriesTerms && setSeriesTranslateRules) {
      setSeriesTranslateRules((prev) => ({
        alwaysTranslate: { ...prev.alwaysTranslate, ...(seriesTerms.alwaysTranslate || {}) },
        neverTranslate: { ...prev.neverTranslate, ...(seriesTerms.neverTranslate || {}) },
      }));
    }
  };

  const scopeLabel = scope === 'series' ? 'Series' : scope === 'account' ? 'Account' : 'Book';
  const confirmScopeAction = useCallback(async () => {
    if (scope === 'book') return true;
    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: `Apply to ${scopeLabel}?`,
        content: 'This affects more than this book. Continue?',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }, [scope, scopeLabel]);

  const applySingleDecision = async (suggestion: TermSuggestion) => {
    if (!run?.runId || !userId) return;
    if (isLockedSuggestion(suggestion)) return;
    let action = decisions[suggestion.id] || getRecommendedAction(suggestion);
    const chosenTarget = resolveChosenTarget(suggestion);
    const conflict = getConflictReason(suggestion.sourceTerm, action, effectiveScope);
    if (conflict) {
      if (conflict === 'series-override') {
        message.info('Already covered by the series glossary. Marking as dismissed for this run.');
        action = 'ignore';
      } else {
        const messageText =
          conflict === 'conflict-never'
            ? 'That term already exists in the Never Translate glossary.'
            : 'That term already exists in the Always Translate glossary.';
        message.warning(messageText);
        return;
      }
    }
    if (action === 'always' && !chosenTarget) {
      message.error('Translation required for Always Translate.');
      return;
    }
    const confirmed = await confirmScopeAction();
    if (!confirmed) return;
    setApplying(true);
    setApplySummary(null);
    setApplyError(null);
    try {
      const scopeValue: ScopeOption = effectiveScope;
      const resp = await axios.post(`${apiBase}/books/${bookId}/term-scan/${run.runId}/apply-single`, {
        userId,
        sourceLang,
        targetLang,
        decision: {
          suggestionId: suggestion.id,
          action,
          targetText: action === 'always' ? chosenTarget : undefined,
          scope: scopeValue,
        },
      });
      const data = resp?.data || {};
      const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
      if (conflicts.length) {
        reportConflicts(conflicts);
        return;
      }
      const updatedSuggestion = data.suggestion && data.suggestion.id ? data.suggestion : null;
      const nextStatus =
        updatedSuggestion?.status ||
        (action === 'ignore' ? 'ignored' : action === 'always' || action === 'never' ? 'applied' : 'pending');
      if (updatedSuggestion) {
        setSuggestions((prev) =>
          prev.map((item) => (item.id === updatedSuggestion.id ? { ...item, ...updatedSuggestion } : item))
        );
      } else {
        setSuggestions((prev) =>
          prev.map((item) =>
            item.id === suggestion.id
              ? {
                  ...item,
                  status: nextStatus,
                  decision: {
                    action,
                    scopes: [scopeValue],
                    targetText: action === 'always' ? chosenTarget : undefined,
                    decidedAt: new Date().toISOString(),
                  },
                }
              : item
          )
        );
      }
      updateLocalTerms(data);
      const appliedNow = nextStatus === 'applied' ? 1 : 0;
      const skippedNow = nextStatus === 'ignored' ? 1 : 0;
      if (appliedNow || skippedNow) {
        setApplySummary(`Applied: ${appliedNow}, Dismissed: ${skippedNow}`);
      }
      setSelectedIds((prev) => {
        if (!prev[suggestion.id]) return prev;
        const next = { ...prev };
        delete next[suggestion.id];
        return next;
      });
      setDirtyIds((prev) => {
        if (!prev[suggestion.id]) return prev;
        const next = { ...prev };
        delete next[suggestion.id];
        return next;
      });
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to apply term decision', err);
      setApplyError(err?.response?.data?.detail || err?.message || 'Failed to apply term decision.');
    } finally {
      setApplying(false);
    }
  };

  const applyDecisions = async () => {
    if (!run?.runId || !userId) return;
    const confirmed = await confirmScopeAction();
    if (!confirmed) return;
    setApplying(true);
    setApplySummary(null);
    setApplyError(null);
    try {
      const selected = suggestions.filter((suggestion) => selectedIds[suggestion.id]);
      if (!selected.length) {
        setApplySummary('Select terms to apply. Unselected terms stay pending.');
        setApplying(false);
        return;
      }

      const actionable = selected.filter(isManuallyActionableSuggestion);

      if (!actionable.length) {
        setApplySummary('No pending selected suggestions to apply.');
        setApplying(false);
        return;
      }

      const skippedConflicts: GlossaryConflict[] = [];
      const missingTargets: string[] = [];
      const payloadDecisions = actionable.reduce((acc, suggestion) => {
        let action = decisions[suggestion.id] || 'ignore';
        const chosenTarget = resolveChosenTarget(suggestion);
        const conflict = getConflictReason(suggestion.sourceTerm, action, effectiveScope);
        if (conflict === 'series-override') {
          action = 'ignore';
        } else if (conflict) {
          skippedConflicts.push(conflict);
          return acc;
        }
        if (action === 'always' && !chosenTarget) {
          missingTargets.push(suggestion.id);
          return acc;
        }
        const scopes: ScopeOption[] = [effectiveScope];
        acc.push({
          suggestionId: suggestion.id,
          action,
          targetText: action === 'always' ? chosenTarget : undefined,
          scopes,
        });
        return acc;
      }, [] as Array<{ suggestionId: string; action: TermAction; targetText?: string; scopes: ScopeOption[] }>);

      const actionableIds = new Set(payloadDecisions.map((item) => item.suggestionId));

      if (!payloadDecisions.length) {
        if (missingTargets.length) {
          message.warning(
            `Left ${missingTargets.length} suggestion${missingTargets.length > 1 ? 's' : ''} for review because ${missingTargets.length > 1 ? 'they are' : 'it is'} missing translations.`
          );
        }
        if (skippedConflicts.length) {
          const seriesCount = skippedConflicts.filter((reason) => reason === 'series-override').length;
          const conflictCount = skippedConflicts.length - seriesCount;
          if (conflictCount) {
            message.warning(`Left ${conflictCount} suggestion${conflictCount > 1 ? 's' : ''} unchanged because of glossary conflicts.`);
          }
          if (seriesCount) {
            message.info(`Left ${seriesCount} suggestion${seriesCount > 1 ? 's' : ''} unchanged because the series glossary already covers ${seriesCount > 1 ? 'them' : 'it'}.`);
          }
        }
        setApplySummary('No valid suggestions to apply.');
        setApplying(false);
        return;
      }

      const resp = await axios.post(`${apiBase}/books/${bookId}/term-scan/${run.runId}/apply`, {
        userId,
        sourceLang,
        targetLang,
        decisions: payloadDecisions,
      });
      const data = resp?.data || {};
      const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
      reportConflicts(conflicts);
      const updatedSuggestions = Array.isArray(data.suggestions) ? data.suggestions : suggestions;
      if (data.suggestions) {
        setSuggestions(data.suggestions || []);
      }
      updateLocalTerms(data);
      if (Array.isArray(updatedSuggestions)) {
        const appliedNow = updatedSuggestions.filter(
          (item: TermSuggestion) => actionableIds.has(item.id) && item.status === 'applied'
        ).length;
        const skippedNow = updatedSuggestions.filter(
          (item: TermSuggestion) => actionableIds.has(item.id) && item.status === 'ignored'
        ).length;
        if (appliedNow || skippedNow) {
          setApplySummary(`Applied: ${appliedNow}, Dismissed: ${skippedNow}`);
        }
        if (missingTargets.length) {
          message.warning(
            `Left ${missingTargets.length} suggestion${missingTargets.length > 1 ? 's' : ''} for review because ${missingTargets.length > 1 ? 'they are' : 'it is'} missing translations.`
          );
        }
        if (skippedConflicts.length) {
          const seriesCount = skippedConflicts.filter((reason) => reason === 'series-override').length;
          const conflictCount = skippedConflicts.length - seriesCount;
          if (conflictCount) {
            message.warning(`Left ${conflictCount} suggestion${conflictCount > 1 ? 's' : ''} unchanged because of glossary conflicts.`);
          }
          if (seriesCount) {
            message.info(`Left ${seriesCount} suggestion${seriesCount > 1 ? 's' : ''} unchanged because the series glossary already covers ${seriesCount > 1 ? 'them' : 'it'}.`);
          }
        }
      }
      setSelectedIds((prev) => {
        const next = { ...prev };
        actionableIds.forEach((id) => {
          if (id && next[id]) {
            delete next[id];
          }
        });
        return next;
      });
      setDirtyIds((prev) => {
        const next = { ...prev };
        actionableIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to apply term decisions', err);
      setApplyError(err?.response?.data?.detail || err?.message || 'Failed to apply term decisions.');
    } finally {
      setApplying(false);
    }
  };

  const handleActionChange = (item: TermSuggestion, action: TermAction) => {
    const conflict = getConflictReason(item.sourceTerm, action, effectiveScope);
    if (conflict) {
      const messageText =
        conflict === 'series-override'
          ? 'Series glossary already covers this term. Book-level rules are not applied.'
          : conflict === 'conflict-never'
          ? 'That term already exists in the Never Translate glossary.'
          : 'That term already exists in the Always Translate glossary.';
      message.warning(messageText);
      return;
    }
    setDecisions((prev) => ({ ...prev, [item.id]: action }));
    setDirtyIds((prev) => ({ ...prev, [item.id]: true }));
  };

  const handleSelectPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = { ...prev };
      pageIds.forEach((id) => {
        if (checked) {
          next[id] = true;
        } else {
          delete next[id];
        }
      });
      return next;
    });
  };

  const updateTargetOverride = useCallback((id: string, value: string) => {
    setTargetOverrides((prev) => ({ ...prev, [id]: value }));
    setDirtyIds((prev) => ({ ...prev, [id]: true }));
  }, []);

  const resolveChosenTarget = useCallback(
    (suggestion: TermSuggestion) => {
      const hasOverride = Object.prototype.hasOwnProperty.call(targetOverrides, suggestion.id);
      const overrideValue = hasOverride ? (targetOverrides[suggestion.id] || '').trim() : '';
      const selectedOption =
        (suggestion.targets && suggestion.targets[0] && suggestion.targets[0].text) || '';
      return (overrideValue || selectedOption || '').trim();
    },
    [targetOverrides]
  );

  const confirmSaveDirtyEdits = useCallback((count: number) => {
    return new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: `Save ${count} unsaved glossary edit${count === 1 ? '' : 's'}?`,
        content:
          'Your edited choices will be saved first. Auto-apply will then add only remaining AI suggestions with at least 85% confidence and will not overwrite saved rules.',
        okText: 'Save edits and continue',
        cancelText: 'Review edits',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }, []);

  const handleAutoApplyConfident = async () => {
    if (!run?.runId || !userId) return;
    const pendingDirty = suggestions.filter(
      (item) => dirtyIds[item.id] && isManuallyActionableSuggestion(item)
    );
    if (!pendingDirty.length && !suggestions.some((item) => (!item.status || item.status === 'pending') && isAutoApplyCandidate(item))) {
      setApplySummary('No pending suggestions meet the 85% confidence policy.');
      return;
    }
    if (pendingDirty.length) {
      const invalidAlways = pendingDirty.filter((item) => {
        const action = decisions[item.id] || getRecommendedAction(item);
        return action === 'always' && !resolveChosenTarget(item);
      });
      if (invalidAlways.length) {
        message.error(
          `Add a translation or choose another action for ${invalidAlways.length} edited term${invalidAlways.length === 1 ? '' : 's'} before continuing.`
        );
        return;
      }
      const confirmed = await confirmSaveDirtyEdits(pendingDirty.length);
      if (!confirmed) return;
    }
    const scopeConfirmed = await confirmScopeAction();
    if (!scopeConfirmed) return;

    setApplying(true);
    setApplySummary(null);
    setApplyError(null);
    const previouslyAppliedCount = suggestions.filter((item) => item.status === 'applied').length;
    let editedSavedCount = 0;
    let editedDismissedCount = 0;
    try {
      if (pendingDirty.length) {
        const editedDecisions = pendingDirty.map((item) => {
          const action = decisions[item.id] || getRecommendedAction(item);
          return {
            suggestionId: item.id,
            action,
            targetText: action === 'always' ? resolveChosenTarget(item) : undefined,
            scopes: [effectiveScope],
          };
        });
        const editedResp = await axios.post(`${apiBase}/books/${bookId}/term-scan/${run.runId}/apply`, {
          userId,
          sourceLang,
          targetLang,
          decisions: editedDecisions,
        });
        const editedData = editedResp?.data || {};
        const conflicts = Array.isArray(editedData.conflicts) ? editedData.conflicts : [];
        reportConflicts(conflicts);
        const editedIds = new Set(pendingDirty.map((item) => item.id));
        const editedSuggestions = Array.isArray(editedData.suggestions) ? editedData.suggestions : [];
        editedSavedCount = editedSuggestions.filter(
          (item: TermSuggestion) => editedIds.has(item.id) && item.status === 'applied'
        ).length;
        editedDismissedCount = editedSuggestions.filter(
          (item: TermSuggestion) => editedIds.has(item.id) && item.status === 'ignored'
        ).length;
        const savedEditedIds: Set<string> = new Set<string>(
          editedSuggestions
            .filter(
              (item: TermSuggestion) =>
                editedIds.has(item.id) && (item.status === 'applied' || item.status === 'ignored')
            )
            .map((item: TermSuggestion): string => item.id)
        );
        if (editedSuggestions.length) setSuggestions(editedSuggestions);
        updateLocalTerms(editedData);
        setDirtyIds((prev) => {
          const next = { ...prev };
          savedEditedIds.forEach((id) => delete next[id]);
          return next;
        });
        if (conflicts.length) {
          setApplyError('Some edited rules conflict with an existing glossary rule. Resolve those edits before Auto Apply.');
          return;
        }
      }

      const resp = await axios.post(
        `${apiBase}/books/${bookId}/term-scan/${run.runId}/auto-apply-confident`,
        {
          userId,
          sourceLang,
          targetLang,
          confidenceThreshold: AUTO_APPLY_CONFIDENCE_THRESHOLD,
          scope: effectiveScope,
        }
      );
      const data = resp?.data || {};
      const result = data.result || {};
      const updatedSuggestions = Array.isArray(result.suggestions) ? result.suggestions : [];
      if (updatedSuggestions.length || Number(data?.summary?.suggestionCount) === 0) {
        setSuggestions(updatedSuggestions);
      } else {
        await fetchRun();
      }
      updateLocalTerms(result);
      const autoApplied = Number(data?.summary?.autoAppliedCount || 0);
      const pending = Number(data?.summary?.pendingReviewCount || 0);
      const parts = [
        `${autoApplied} confident AI suggestion${autoApplied === 1 ? '' : 's'} applied`,
      ];
      if (editedSavedCount) {
        parts.push(`${editedSavedCount} edited rule${editedSavedCount === 1 ? '' : 's'} saved first`);
      }
      if (editedDismissedCount) {
        parts.push(`${editedDismissedCount} edited suggestion${editedDismissedCount === 1 ? '' : 's'} dismissed`);
      }
      if (previouslyAppliedCount) {
        parts.push(`${previouslyAppliedCount} previously applied rule${previouslyAppliedCount === 1 ? '' : 's'} unchanged`);
      }
      parts.push(`${pending} left to review`);
      setApplySummary(`${parts.join('; ')}.`);
      handleStatusChange('pending');
      setUncertainOnly(false);
      scrollListToTop();
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to auto-apply glossary suggestions', err);
      setApplyError(err?.response?.data?.detail || err?.message || 'Failed to auto-apply glossary suggestions.');
    } finally {
      setApplying(false);
    }
  };

  const handleRegenerateSuggestion = async (suggestion: TermSuggestion) => {
    if (!run?.runId || !userId || !suggestion.id) return;
    setRegeneratingIds((prev) => ({ ...prev, [suggestion.id]: true }));
    setApplyError(null);
    try {
      const resp = await axios.post(
        `${apiBase}/books/${bookId}/term-scan/${run.runId}/suggestions/${suggestion.id}/regenerate`,
        { userId, sourceLang, targetLang }
      );
      const updated = resp?.data?.suggestion as TermSuggestion | undefined;
      if (!updated?.id) throw new Error('No regenerated suggestion was returned.');
      setSuggestions((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
      const regeneratedAction: TermAction =
        updated.classification === 'always' && Boolean(updated.targets?.length)
          ? 'always'
          : getRecommendedAction(updated);
      setDecisions((prev) => ({ ...prev, [updated.id]: regeneratedAction }));
      setTargetOverrides((prev) => {
        const next = { ...prev };
        delete next[updated.id];
        return next;
      });
      setDirtyIds((prev) => {
        const next = { ...prev };
        delete next[updated.id];
        return next;
      });
      if (updated.classification === 'always' && updated.targets?.length) {
        message.success('Generated a context-aware glossary translation. Review it before applying.');
      } else {
        message.info('No safe fixed translation was found. This term can be translated normally in context.');
      }
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to regenerate glossary suggestion', err);
      setApplyError(err?.response?.data?.detail || err?.message || 'Unable to regenerate this suggestion.');
    } finally {
      setRegeneratingIds((prev) => ({ ...prev, [suggestion.id]: false }));
    }
  };

  const neutralTagStyle = {
    background: 'var(--surface-muted)',
    borderColor: 'var(--border-subtle)',
    color: 'var(--text-secondary)',
  };
  const seriesCoverageTagStyle = {
    background: 'var(--surface-info)',
    borderColor: 'var(--border-info)',
    color: 'var(--text-info)',
  };
  const scopeTagStyle = {
    background: 'var(--surface-muted)',
    borderColor: 'var(--border-subtle)',
    color: 'var(--text-secondary)',
  };

  const renderSuggestion = (item: TermSuggestion) => {
    const action = decisions[item.id] || getRecommendedAction(item);
    const targets = item.targets || [];
    const defaultTarget = targets.length ? targets[0].text : '';
    const hasOverride = Object.prototype.hasOwnProperty.call(targetOverrides, item.id);
    const selectedTarget = hasOverride ? targetOverrides[item.id] : defaultTarget;
    const hasSelectedTarget = Boolean((selectedTarget || '').trim());
    const classification = getClassification(item);
    const classificationLabel =
      classification === 'always'
        ? 'Translate'
        : classification === 'never'
        ? 'Keep original'
        : 'Review needed';
    const confidenceLabel =
      typeof item.confidence === 'number' && Number.isFinite(item.confidence)
        ? `${Math.round(item.confidence * 100)}%`
        : '';
    const recommendationTooltip =
      classification === 'always'
        ? 'AI recommends using the same translation every time.'
        : classification === 'never'
        ? 'AI recommends keeping this term in its original form.'
        : 'AI could not recommend a glossary rule confidently. Review this term.';
    const isLocked = isLockedSuggestion(item);
    const isSkipped = isSkippedSuggestion(item);
    const isSelected = Boolean(selectedIds[item.id]);
    const isDirty = Boolean(dirtyIds[item.id]);
    const isRegenerating = Boolean(regeneratingIds[item.id]);
    const hasMultipleTargets = targets.length > 1;
    const contextSnippets = Array.from(
      new Set([...(item.contextSnippets || []), item.snippet || ''].map((value) => value.trim()).filter(Boolean))
    ).slice(0, 3);
    const selectedTargetEntry =
      targets.find((target) => target.text === selectedTarget);
    const selectedMeaning = selectedTargetEntry?.meaning;
    const occurrenceCount = typeof item.occurrenceCount === 'number' ? item.occurrenceCount : null;
    const sourceKey = normalizeTerm(item.sourceTerm);
    const alwaysAlreadyExists =
      Boolean(sourceKey) &&
      (effectiveScope === 'book'
        ? bookAlwaysSourceSet.has(sourceKey)
        : effectiveScope === 'series'
        ? seriesAlwaysSourceSet.has(sourceKey)
        : accountAlwaysSourceSet.has(sourceKey));
    const alwaysDisabled =
      Boolean(sourceKey) &&
      (neverConflictSet.has(sourceKey) ||
        (effectiveScope === 'book' && seriesAlwaysSourceSet.has(sourceKey)) ||
        alwaysAlreadyExists);
    const neverDisabled =
      Boolean(sourceKey) &&
      (alwaysConflictSet.has(sourceKey) ||
        (effectiveScope === 'book' && seriesNeverSet.has(sourceKey)));
    const inferredSeriesCoverage =
      Boolean(sourceKey) && effectiveScope === 'book'
        ? seriesAlwaysSourceSet.has(sourceKey)
          ? 'always'
          : seriesNeverSet.has(sourceKey)
          ? 'never'
          : null
        : null;
    const persistedSeriesCoverage =
      item.seriesCoverage === 'always' || item.seriesCoverage === 'never' ? item.seriesCoverage : null;
    const showSeriesCoveredTag =
      Boolean(inferredSeriesCoverage || persistedSeriesCoverage) || item.decision?.reason === 'series-override';
    const seriesCoverageDetailLabel =
      inferredSeriesCoverage === 'always' || persistedSeriesCoverage === 'always'
        ? 'Series: Always Translate'
        : inferredSeriesCoverage === 'never' || persistedSeriesCoverage === 'never'
        ? 'Series: Never Translate'
        : 'Covered by series glossary';
    const persistedScopeValues = resolveSuggestionDecisionScopes(item.decision);
    const hasDecisionMetadata = Boolean(item.decision && Object.keys(item.decision).length > 0);
    const showScopeTags = isLocked || hasDecisionMetadata;
    const scopeLabels = persistedScopeValues.length
      ? persistedScopeValues.map((scopeValue) => `Scope: ${formatScopeOptionLabel(scopeValue)}`)
      : ['Scope: Unknown'];
    const isTutorialFirstSuggestion = item.id === tutorialFirstSuggestionId;
    const isTutorialAlwaysSuggestion = item.id === tutorialAlwaysSuggestionId;
    const applyButton = (
      <Button
        className={`term-scan-suggestion__apply${action === 'always' ? ' is-inline' : ''}`}
        size="small"
        type={isDirty ? 'primary' : 'default'}
        onClick={() => applySingleDecision(item)}
        disabled={!userId || isLocked || applying}
        data-testid={
          isTutorialAlwaysSuggestion
            ? 'tutorial:term-scan:always-suggestion-apply'
            : isTutorialFirstSuggestion
            ? 'tutorial:term-scan:first-suggestion-apply'
            : undefined
        }
      >
        Apply
      </Button>
    );
    return (
      <List.Item
        key={item.id}
        className={`term-scan-suggestion${isSelected ? ' is-selected' : ''}${isDirty ? ' is-dirty' : ''}`}
        data-testid={
          isTutorialAlwaysSuggestion
            ? 'tutorial:term-scan:always-suggestion'
            : isTutorialFirstSuggestion
            ? 'tutorial:term-scan:first-suggestion'
            : undefined
        }
      >
        <div className="term-scan-suggestion__body">
          <div className="term-scan-suggestion__header">
            <div className="term-scan-suggestion__identity">
              <Checkbox
                checked={isSelected}
                onChange={(event) =>
                  setSelectedIds((prev) => ({ ...prev, [item.id]: event.target.checked }))
                }
                disabled={!item.id || isLocked}
                aria-label={`Select ${item.sourceTerm}`}
                data-testid={isTutorialFirstSuggestion ? 'tutorial:term-scan:first-suggestion-select' : undefined}
              />
              <Text strong className="term-scan-suggestion__term">{item.sourceTerm}</Text>
            </div>
            <div className="term-scan-suggestion__badges">
              <Tooltip
                title={`${recommendationTooltip}${confidenceLabel ? ` Confidence: ${confidenceLabel}.` : ''}`}
                placement="top"
              >
                <Tag className="term-scan-suggestion__recommendation">
                  {classificationLabel}{confidenceLabel ? ` · ${confidenceLabel}` : ''}
                </Tag>
              </Tooltip>
              {isSkipped && <Tag style={neutralTagStyle}>Dismissed</Tag>}
              {isDirty && <Tag color="gold">Unsaved edit</Tag>}
              {showSeriesCoveredTag && <Tag style={seriesCoverageTagStyle}>Covered by series glossary</Tag>}
              {showSeriesCoveredTag && seriesCoverageDetailLabel !== 'Covered by series glossary' && (
                <Tag style={neutralTagStyle}>{seriesCoverageDetailLabel}</Tag>
              )}
              {showScopeTags &&
                scopeLabels.map((label) => (
                  <Tag key={`${item.id}-${label}`} style={scopeTagStyle}>
                    {label}
                  </Tag>
                ))}
              {occurrenceCount != null && (
                <Tag style={neutralTagStyle}>{occurrenceCount} use{occurrenceCount === 1 ? '' : 's'}</Tag>
              )}
            </div>
          </div>
          {(item.rationale || contextSnippets.length > 0) && (
            <div className="term-scan-suggestion__metadata">
              {item.rationale && (
                <Text type="secondary" className="term-scan-suggestion__rationale">
                  {item.rationale}
                </Text>
              )}
              {contextSnippets.length > 0 && (
                <details className="term-scan-context">
                  <summary>View manuscript context ({contextSnippets.length})</summary>
                  <Space direction="vertical" size={4} className="term-scan-context__content">
                    {contextSnippets.map((snippet, index) => (
                      <Text key={`${item.id}-context-${index}`} type="secondary" italic>
                        “{snippet}”
                      </Text>
                    ))}
                  </Space>
                </details>
              )}
            </div>
          )}
          <div className={`term-scan-suggestion__editor ${action === 'always' ? '' : 'is-single-field'}`}>
            <div className="term-scan-suggestion__field">
              <Text className="term-scan-suggestion__field-label">Decision</Text>
              <Select<TermAction>
                value={action}
                onChange={(value) => handleActionChange(item, value)}
                disabled={isLocked}
                options={[
                  { value: 'always', label: 'Always translate', disabled: alwaysDisabled },
                  { value: 'never', label: 'Never translate', disabled: neverDisabled },
                  { value: 'ignore', label: 'Translate normally in context — no rule' },
                ]}
                style={{ width: '100%' }}
                size="small"
                data-testid={
                  isTutorialAlwaysSuggestion
                    ? 'tutorial:term-scan:always-suggestion-action'
                    : isTutorialFirstSuggestion
                    ? 'tutorial:term-scan:first-suggestion-action'
                    : undefined
                }
              />
            </div>
            {action === 'always' && (
              <div className="term-scan-suggestion__field">
                <div className="term-scan-suggestion__field-heading">
                  <Text className="term-scan-suggestion__field-label">Translation to use every time</Text>
                  {selectedMeaning && (
                    <Tooltip title={selectedMeaning} placement="top">
                      <InfoCircleOutlined
                        className="term-scan-suggestion__meaning-info"
                        aria-label={`Translation note: ${selectedMeaning}`}
                      />
                    </Tooltip>
                  )}
                </div>
                <div className="term-scan-suggestion__translation-control">
                  {targets.length > 1 ? (
                    <AutoComplete
                      value={selectedTarget}
                      onChange={(val) => updateTargetOverride(item.id, (val || '').toString())}
                      disabled={isLocked}
                      options={targets.map((t) => ({
                        value: t.text,
                        label: t.text,
                      }))}
                      className="term-scan-suggestion__translation-input"
                      data-testid={isTutorialAlwaysSuggestion ? 'tutorial:term-scan:always-suggestion-target' : undefined}
                      filterOption={(input, option) => {
                        const query = (input || '').toString().toLowerCase();
                        if (!query || query === selectedTarget.toLowerCase()) {
                          return true;
                        }
                        return (option?.value || '').toString().toLowerCase().includes(query);
                      }}
                    >
                      <Input
                        placeholder="Translation"
                        size="small"
                        suffix={<DownOutlined />}
                      />
                    </AutoComplete>
                  ) : (
                    <Input
                      value={selectedTarget}
                      onChange={(event) => updateTargetOverride(item.id, event.target.value)}
                      placeholder={defaultTarget ? 'Translation' : 'No fixed translation generated'}
                      className="term-scan-suggestion__translation-input"
                      size="small"
                      disabled={isLocked}
                      data-testid={isTutorialAlwaysSuggestion ? 'tutorial:term-scan:always-suggestion-target' : undefined}
                    />
                  )}
                  {hasMultipleTargets && <Tag style={neutralTagStyle}>{targets.length} options</Tag>}
                  {!hasSelectedTarget && !isLocked && (
                    <Tooltip
                      title={
                        contextSnippets.length
                          ? 'Generate a translation using the manuscript context.'
                          : 'No manuscript context is available for this term.'
                      }
                    >
                      <Button
                        size="small"
                        aria-label="Generate with context"
                        onClick={() => void handleRegenerateSuggestion(item)}
                        loading={isRegenerating}
                        disabled={!contextSnippets.length || applying}
                      >
                        Generate
                      </Button>
                    </Tooltip>
                  )}
                  {applyButton}
                </div>
                {!hasSelectedTarget && !isLocked && (
                  <Text type="secondary" className="term-scan-suggestion__translation-help">
                    Enter a translation, generate one from the excerpts, or choose “Translate normally in context.”
                  </Text>
                )}
              </div>
            )}
            {action !== 'always' && applyButton}
          </div>
        </div>
      </List.Item>
    );
  };

  const runStatus = (run?.status || '').toLowerCase();
  const isActive = isActiveGlossaryScan(runStatus);
  const isFailed = runStatus === 'failed';
  const isCancelled = runStatus === 'cancelled' || runStatus === 'canceled';
  const pendingSuggestions = filteredSuggestions.filter(
    (item) => !item.status || item.status === 'pending'
  );
  const confidentCount = pendingSuggestions.filter(
    (item) => isAutoApplyCandidate(item)
  ).length;
  const aiApplyCount = confidentCount;
  const viewPendingCount = pendingSuggestions.length;
  const pageSelectionLabel = pageIds.length ? `Select this page (${pageIds.length})` : 'Select this page';
  const remainingAfterAutoApply = Math.max(0, viewPendingCount - aiApplyCount);
  const aiHelperText = aiApplyCount
    ? `Applies ${aiApplyCount} suggestion${aiApplyCount === 1 ? '' : 's'} at or above 85% confidence. ${remainingAfterAutoApply} left for review.`
    : 'No pending suggestions meet the 85% confidence policy.';
  const totalCount = filteredSuggestions.length;
  const startIndex = totalCount ? (currentPage - 1) * pageSize + 1 : 0;
  const endIndex = totalCount ? Math.min(currentPage * pageSize, totalCount) : 0;
  const resultsLabel = totalCount ? `Showing ${startIndex}-${endIndex} of ${totalCount}` : '0 total';
  const reviewedCount = Math.max(0, suggestions.length - pendingCount);
  const completionPercent = suggestions.length ? Math.round((reviewedCount / suggestions.length) * 100) : 0;
  const summaryLine = `${reviewedCount} reviewed · ${pendingCount} remaining · ${suggestions.length} total`;
  const statusOptions = [
    { label: `Pending (${pendingCount})`, value: 'pending' as StatusFilter },
    { label: `Applied (${appliedCount})`, value: 'applied' as StatusFilter },
    { label: `Dismissed (${manualSkippedCount})`, value: 'skipped' as StatusFilter },
    { label: `Covered (${seriesCoveredCount})`, value: 'covered' as StatusFilter },
    { label: `All (${suggestions.length})`, value: 'all' as StatusFilter },
  ];
  const scopeOptions = [
    { label: 'Book', value: 'book' as ScopeOption },
    { label: 'Series', value: 'series' as ScopeOption, disabled: !canApplySeries },
    { label: 'Account', value: 'account' as ScopeOption, disabled: !canApplyAccount },
  ];
  const filtersContent = (
    <Space direction="vertical" size="small" style={{ minWidth: 220 }}>
      {!showStatusTabs && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Status
          </Text>
          <Select<StatusFilter>
            value={statusFilter}
            onChange={handleStatusChange}
            options={[
              { value: 'pending', label: 'Pending' },
              { value: 'applied', label: 'Applied' },
              { value: 'skipped', label: 'Dismissed' },
              { value: 'covered', label: 'Covered' },
              { value: 'all', label: 'All statuses' },
            ]}
            style={{ width: '100%' }}
            size="small"
          />
        </div>
      )}
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Applied scope
        </Text>
        <Select<ScopeFilter>
          value={scopeFilter}
          onChange={(value) => {
            setScopeFilter(value);
            setCurrentPage(1);
            clearSelection();
          }}
          options={[
            { value: 'all', label: 'All scopes' },
            { value: 'book', label: 'Book' },
            { value: 'series', label: 'Series' },
            { value: 'account', label: 'Account' },
            { value: 'unknown', label: 'Unknown / Legacy' },
          ]}
          style={{ width: '100%' }}
          size="small"
        />
      </div>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Min occurrences
        </Text>
        <InputNumber
          min={1}
          precision={0}
          value={minOccurrences ?? undefined}
          onChange={(value) => {
            const next = typeof value === 'number' && Number.isFinite(value) ? value : null;
            setMinOccurrences(next);
            setCurrentPage(1);
            clearSelection();
          }}
          placeholder="Min occurrences"
          style={{ width: '100%' }}
          size="small"
        />
      </div>
      <Checkbox
        checked={onlyMultiOption}
        onChange={(event) => {
          setOnlyMultiOption(event.target.checked);
          setCurrentPage(1);
          clearSelection();
        }}
      >
        Multi-option only
      </Checkbox>
      <Checkbox
        checked={uncertainOnly}
        onChange={(event) => {
          setUncertainOnly(event.target.checked);
          setCurrentPage(1);
          clearSelection();
        }}
        disabled={statusFilter !== 'pending'}
      >
        Uncertain only
      </Checkbox>
    </Space>
  );
  const activeFilterCount =
    (scopeFilter !== 'all' ? 1 : 0) +
    (minOccurrences != null ? 1 : 0) +
    (onlyMultiOption ? 1 : 0) +
    (uncertainOnly ? 1 : 0) +
    (!showStatusTabs && statusFilter !== 'pending' ? 1 : 0);
  const clearFilters = () => {
    setScopeFilter('all');
    setMinOccurrences(null);
    setOnlyMultiOption(false);
    setUncertainOnly(false);
    if (!showStatusTabs) {
      setStatusFilter('pending');
    }
    setCurrentPage(1);
    clearSelection();
  };
  const showLoadingState = loading && suggestions.length === 0;
  const showSkippedSummary = manualSkippedCount > 0 || seriesCoveredCount > 0;
  const emptyListText = run
    ? suggestions.length
      ? 'No matches for the current filters.'
      : isActive
      ? 'Scan in progress. Suggestions will appear after completion.'
      : 'No suggestions found for this AI glossary scan.'
    : 'Run an AI glossary scan to see suggestions.';

  const tourSteps = useMemo<TourProps['steps']>(() => {
    const steps: NonNullable<TourProps['steps']> = [
      {
        title: 'Glossary scan summary',
        description: renderTeacherPanel(
          <>Review total suggestions, pending counts, and how many glossary rules are already in place.</>
        ),
        target: () => getTourTarget(['term-scan-summary', 'term-scan-panel']),
        placement: 'bottom',
      },
    ];

    steps.push({
      title: showStatusTabs ? 'Status tabs' : 'Status filter',
      description: renderTeacherPanel(
        showStatusTabs ? (
          <>Switch between pending, applied, dismissed, or all suggestions.</>
        ) : (
          <>Use filters to narrow by status when tabs are hidden on smaller screens.</>
        )
      ),
      target: () => getTourTarget(['term-scan-status-tabs', 'term-scan-filters', 'term-scan-panel']),
      placement: 'bottom',
    });

    steps.push({
      title: 'Search & sort',
      description: renderTeacherPanel(
        <>Find specific terms, adjust sort order, and open filters for more control.</>
      ),
      target: () => getTourTarget(['term-scan-toolbar', 'term-scan-search', 'term-scan-sort', 'term-scan-filters']),
      placement: 'bottomLeft',
    });

    if (filteredSuggestions.length === 0) {
      steps.push({
        title: isActive ? 'Suggestions are still processing' : 'No suggestions to review',
        description: renderTeacherPanel(
          isActive ? (
            <>The scan is still running. Suggestions and bulk controls will appear here after completion.</>
          ) : (
            <>There are no terms in the current view. Adjust filters or run another scan before using bulk actions.</>
          )
        ),
        target: () => getTourTarget(['term-scan-list', 'term-scan-panel']),
        placement: 'top',
      });
      return steps;
    }

    steps.push({
      title: 'Select terms',
      description: renderTeacherPanel(
        <>Select this page to apply decisions in bulk or review a subset of suggestions.</>
      ),
      target: () => getTourTarget(['term-scan-select-page', 'term-scan-actions']),
      placement: 'bottomLeft',
    });

    steps.push({
      title: 'Scope & auto-apply',
      description: renderTeacherPanel(
        <>
          Choose where rules apply before bulk accepting terms: book rules stay local, series rules
          cascade to books in the series, and account rules affect future matching work. Auto-apply
          accepts AI recommendations for the selected scope.
        </>
      ),
      target: () => getTourTarget(['term-scan-scope-actions', 'term-scan-scope', 'term-scan-auto-apply', 'term-scan-auto-mode']),
      placement: 'bottomRight',
    });

    steps.push({
      title: 'Review suggestions',
      description: renderTeacherPanel(
        <>
          Each term includes AI guidance, optional translation targets, and an Apply button for manual approval.
        </>
      ),
      target: () => getTourTarget(['term-scan-list', 'term-scan-panel']),
      placement: 'top',
    });

    return steps;
  }, [filteredSuggestions.length, getTourTarget, isActive, renderTeacherPanel, showStatusTabs]);

  const isTourReady = useCallback(() => {
    if (showLoadingState) return false;
    if (typeof document === 'undefined') return false;
    return Boolean(document.querySelector('[data-tour-id="term-scan-summary"]'));
  }, [showLoadingState]);

  useEffect(() => {
    if (tourOpen) return;
    if (!tourFlagsInitialized) return;
    if (hasSeenTermScanTour) return;
    let cancelled = false;
    const tryOpen = () => {
      if (cancelled) return;
      if (!isTourReady()) {
        window.setTimeout(tryOpen, 120);
        return;
      }
      setTourOpen(true);
    };
    const timer = window.setTimeout(tryOpen, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hasSeenTermScanTour, isTourReady, tourFlagsInitialized, tourOpen]);

  return (
    <div
      ref={tourFallbackRef}
      className="term-scan-panel-shell"
      data-tour-id="term-scan-panel"
      data-testid="tutorial:term-scan:review-panel"
    >
      <section className="term-scan-review-panel" aria-labelledby="term-scan-review-title">
      <Space direction="vertical" className="term-scan-review-stack" size="small">
        <div className="term-scan-review-header">
          <div className="term-scan-review-heading">
            <div className="term-scan-review-kicker">AI glossary scan</div>
            <h3 id="term-scan-review-title">Review suggestions</h3>
            <div
              className="term-scan-progress-summary"
              data-tour-id="term-scan-summary"
              data-testid="tutorial:term-scan:summary"
            >
              <div
                className="term-scan-progress-track"
                role="progressbar"
                aria-label="Glossary review progress"
                aria-valuemin={0}
                aria-valuemax={Math.max(1, suggestions.length)}
                aria-valuenow={reviewedCount}
              >
                <span className="term-scan-progress-fill" style={{ width: `${completionPercent}%` }} />
              </div>
              <span>{summaryLine}</span>
            </div>
          </div>
          <div className="term-scan-review-header-actions">
            {!onTourControlChange && (
              <Button
                type="text"
                size="small"
                icon={<QuestionCircleOutlined />}
                onClick={handleOpenTour}
                className="app-tour-trigger"
              >
                Take a tour
              </Button>
            )}
            <Tooltip title={!run?.runId ? 'Run an AI glossary scan before exporting.' : undefined}>
              <span>
                <Button
                  type="text"
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={handleExportDocx}
                  loading={exportingDocx}
                  disabled={!run?.runId || !userId}
                >
                  Export Word
                </Button>
              </span>
            </Tooltip>
            <span className={`term-scan-review-status ${isFailed ? 'is-error' : isActive ? 'is-running' : pendingCount ? 'is-needed' : 'is-ready'}`}>
              {isCancelled ? 'Cancelled' : isFailed ? 'Failed' : runStatus === 'cancelling' ? 'Cancelling' : isActive ? 'Running' : pendingCount ? `${pendingCount} pending` : 'Reviewed'}
            </span>
          </div>
        </div>
        {loading && suggestions.length > 0 && (
          <AntAlert type="info" showIcon message="Refreshing suggestions" className="term-scan-review-alert" />
        )}
        {fetchError && (
          <AntAlert
            type="error"
            showIcon
            message="Unable to refresh scan suggestions"
            description={fetchError}
            action={
              <Button size="small" onClick={() => void fetchRun()} loading={loading}>
                Retry
              </Button>
            }
            className="term-scan-review-alert"
          />
        )}
        {isCancelled && <AntAlert type="info" showIcon message="Scan cancelled. Uncommitted reserved credits were released." className="term-scan-review-alert" />}
        {isFailed && run?.error && (
          <AntAlert type="error" showIcon message="Scan failed" description={run.error} className="term-scan-review-alert" />
        )}
        {applySummary && <AntAlert type="success" showIcon message={applySummary} className="term-scan-review-alert" />}
        {applyError && <AntAlert type="error" showIcon message={applyError} className="term-scan-review-alert" />}
        {!canScan && (
          <AntAlert
            type="info"
            showIcon
            message="Select a target language to run an AI glossary scan."
            className="term-scan-review-alert"
          />
        )}
        {isActive && (
          <AntAlert
            type="info"
            showIcon
            message={runStatus === 'cancelling'
              ? 'Cancellation requested. The scan will stop after its current work; unused credits will be released.'
              : 'AI glossary scan is running. Results appear after the scan completes.'}
            className="term-scan-review-alert"
          />
        )}
        {showSkippedSummary && (
          <div className="term-scan-skipped-summary" role="note" data-testid="tutorial:term-scan:skipped-summary">
            {manualSkippedCount > 0 && (
              <div className="term-scan-skipped-summary__item">
                <Text strong>Dismissed</Text>
                <Text type="secondary">{manualSkippedCount} dismissed suggestion{manualSkippedCount === 1 ? '' : 's'} can be changed here.</Text>
              </div>
            )}
            {seriesCoveredCount > 0 && (
              <div className="term-scan-skipped-summary__item">
                <Text strong>Covered</Text>
                <Text type="secondary">{seriesCoveredCount} term{seriesCoveredCount === 1 ? '' : 's'} covered by the series glossary are locked here.</Text>
              </div>
            )}
          </div>
        )}
        {showStatusTabs && (
          <Segmented
            size={controlSize}
            options={statusOptions}
            value={statusFilter}
            onChange={(value) => handleStatusChange(value as StatusFilter)}
            className="term-scan-status-tabs"
            style={{
              background: 'var(--surface-muted)',
              border: '1px solid var(--border-subtle)',
            }}
            data-tour-id="term-scan-status-tabs"
            data-testid="tutorial:term-scan:status-tabs"
          />
        )}
        <div
          className="term-scan-review-toolbar"
          data-tour-id="term-scan-toolbar"
          data-testid="tutorial:term-scan:toolbar"
        >
          <div className="term-scan-review-toolbar__controls">
            <Input
              placeholder="Search terms"
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
                clearSelection();
              }}
              allowClear
              className="term-scan-review-search"
              size={controlSize}
              data-tour-id="term-scan-search"
              data-testid="tutorial:term-scan:search"
            />
            <Select<SortOption>
              aria-label="Sort suggestions"
              value={sortOption}
              onChange={(value) => {
                setSortOption(value);
                setCurrentPage(1);
                clearSelection();
              }}
              options={[
                { value: 'term-asc', label: 'Sort: Term A-Z' },
                { value: 'occurrences-desc', label: 'Sort: Most occurrences' },
              ]}
              className="term-scan-review-sort"
              size={controlSize}
              data-tour-id="term-scan-sort"
              data-testid="tutorial:term-scan:sort"
            />
            {useDrawerFilters ? (
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFiltersOpen(true)}
                size={controlSize}
                data-tour-id="term-scan-filters"
                data-testid="tutorial:term-scan:filters"
              >
                Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
              </Button>
            ) : (
              <Popover
                content={filtersContent}
                trigger="click"
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                placement="bottomLeft"
              >
                <Button icon={<FilterOutlined />} size={controlSize} data-tour-id="term-scan-filters" data-testid="tutorial:term-scan:filters">
                  Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
                </Button>
              </Popover>
            )}
          </div>
          <div
            className="term-scan-review-results"
            data-tour-id="term-scan-results"
            data-testid="tutorial:term-scan:results"
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              {resultsLabel}
            </Text>
            {activeFilterCount > 0 && (
              <Button type="link" size="small" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </div>
        </div>
        <div
          className="term-scan-review-actions"
          data-tour-id="term-scan-actions"
          data-testid="tutorial:term-scan:actions"
        >
          <div className="term-scan-review-actions__selection">
            <Checkbox
              checked={allPageSelected}
              indeterminate={somePageSelected}
              onChange={(event) => handleSelectPage(event.target.checked)}
              disabled={!pageIds.length}
              data-tour-id="term-scan-select-page"
              data-testid="tutorial:term-scan:select-page"
            >
              {pageSelectionLabel}
            </Checkbox>
            <div className="term-scan-review-actions__copy">
              <Text strong>
                {selectedCount > 0
                  ? `${selectedCount} selected`
                  : aiApplyCount
                  ? `${aiApplyCount} ready to apply automatically`
                  : 'Review these suggestions individually'}
              </Text>
              <Text
                type="secondary"
                style={{ fontSize: 12 }}
                data-tour-id="term-scan-auto-mode"
                data-testid="tutorial:term-scan:auto-mode"
              >
                {selectedCount > 0
                  ? 'Apply the decisions currently shown in the selected rows.'
                  : `${aiHelperText}${dirtyCount ? ` ${dirtyCount} unsaved edit${dirtyCount === 1 ? '' : 's'} will be saved first.` : ''}`}
              </Text>
            </div>
          </div>
          <div
            className="term-scan-review-actions__buttons"
            data-tour-id="term-scan-scope-actions"
            data-testid="tutorial:term-scan:scope-actions"
          >
            <div className="term-scan-review-actions__scope">
              <Tooltip
                title={
                  scope === 'book'
                    ? 'Applies changes to this book only.'
                    : 'This affects more than this book.'
                }
              >
                <Text type="secondary" style={{ fontSize: 12 }}>Save rules to</Text>
              </Tooltip>
              <Tooltip
                title={
                  scope === 'book'
                    ? 'Applies changes to this book only.'
                    : 'This affects more than this book.'
                }
              >
                <Select<ScopeOption>
                  aria-label="Glossary destination scope"
                  value={scope}
                  onChange={handleScopeChange}
                  options={scopeOptions}
                  style={{ width: 140 }}
                  size={controlSize}
                  status={scope === 'book' ? undefined : 'warning'}
                  data-tour-id="term-scan-scope"
                  data-testid="tutorial:term-scan:scope"
                />
              </Tooltip>
            </div>
            {selectedCount > 0 ? (
              <Tooltip title={!userId ? 'Sign in to apply suggestions.' : undefined}>
                <span>
                  <Button
                    onClick={() => applyDecisions()}
                    disabled={!selectedActionableCount || !userId}
                    loading={applying}
                    type="primary"
                    size={controlSize}
                  >
                    Apply selected ({selectedCount})
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  !userId
                    ? 'Sign in to apply suggestions.'
                    : aiApplyCount
                    ? aiHelperText
                    : dirtyCount
                    ? 'Save edited rows first, then apply any remaining suggestions that meet the 85% confidence policy.'
                    : 'No pending suggestions meet the 85% confidence policy.'
                }
              >
                <span>
                  <Button
                    type="primary"
                    icon={<RobotOutlined />}
                    onClick={() => void handleAutoApplyConfident()}
                    disabled={(!aiApplyCount && !dirtyCount) || !userId}
                    loading={applying}
                    size={controlSize}
                    data-tour-id="term-scan-auto-apply"
                    data-testid="tutorial:term-scan:auto-apply"
                  >
                    Auto-apply confident AI{aiApplyCount ? ` (${aiApplyCount})` : ''}
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </Space>
      {useDrawerFilters && (
        <Drawer
          title="Filters"
          placement="bottom"
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          height="auto"
          styles={{ wrapper: { maxHeight: '70vh' } }}
        >
          {filtersContent}
        </Drawer>
      )}
      <div
        ref={listContainerRef}
        className="term-scan-review-list"
        style={{
          maxHeight: '59vh',
          overflowY: 'auto',
        }}
        data-tour-id="term-scan-list"
        data-testid="tutorial:term-scan:list"
      >
        {showLoadingState ? (
          <div className="loading-card-wrapper" style={{ padding: '40px 0' }}>
            <LoadingCard label="Loading glossary suggestions..." />
          </div>
        ) : (
          <List
            dataSource={pageItems}
            locale={{ emptyText: emptyListText }}
            renderItem={renderSuggestion}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredSuggestions.length,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
              size: 'small',
            }}
          />
        )}
      </div>
    </section>
    <AppTour
      open={tourOpen}
      onClose={handleCloseTour}
      onFinish={handleCloseTour}
      steps={tourSteps || []}
      zIndex={2600}
      mask={{ style: { pointerEvents: 'none' } }}
    />
    </div>
  );
};

export default TermScanPanel;
